import classnames from "classnames";

interface TextButtonPropsBase {
  /**
   * @type string
   * @description Tailwind CSS string to override default styling
   */
  className?: string;
  /**
   * @type any
   * @description Any child components.
   */
  children?: any;
  /**
   * @type String
   * @description Text for the button.
   */
  text: string;
  /**
   * @description Pass this prop to render a disabled button.
   */
  disabled?: boolean;
  /**
   * @type String
   * @description Fontawesome icon string. Icon to be added on the left of the text.
   * @example "fas fa-check"
   */
  iconLeft?: string;
  /**
   * @type String
   * @description Fontawesome icon string. Icon to be added on the right of the text.
   * @example "fas fa-check"
   */
  iconRight?: string;
  /**
   * @type String
   * @description String representing the test id.
   */
  dataTestId?: string;
}

interface TextButtonPropsHref extends TextButtonPropsBase {
  /**
   * @type String
   * @description String representing the href link.
   */
  href: string;
  /**
   * @type String
   * @description String representing the downloaded file name.
   */
  download?: string;
}

interface TextButtonPropsOnClick extends TextButtonPropsBase {
  /**
   * @type (e) => void
   * @description Function to be called on clicking the button.
   */
  onClick: (e?: React.MouseEvent<HTMLButtonElement>) => void;
}

/**
 * @description
 * Required Props: `text`, one of `href` or `onClick`
 *
 * Optional Props: `className`, `children`, `disabled`, `iconLeft`, `iconRight`, `download`
 */
export function TextButton(
  props: TextButtonPropsOnClick | TextButtonPropsHref,
) {
  const iconLeftHTML = props.iconLeft && (
    <i className={`${props.iconLeft} tw-mr-[8px]`} />
  );
  const iconRightHTML = props.iconRight && (
    <i className={`${props.iconRight} tw-ml-[8px]`} />
  );
  return (
    <button
      className={classnames("text-btn", props.className)}
      onClick={"onClick" in props ? props.onClick : undefined}
      onKeyDown={(e) => {
        if (e.key === "Enter" && "onClick" in props) {
          props.onClick();
        }
      }}
      disabled={props.disabled}
      data-testid={props.dataTestId}
    >
      {!props.disabled && "href" in props ? (
        <a
          className="tw-text-inherit hover:tw-text-blue-dark-1"
          href={props.href}
          download={props.download}
        >
          {iconLeftHTML}
          {props.text}
          {iconRightHTML}
        </a>
      ) : (
        <span>
          {iconLeftHTML}
          {props.text}
          {iconRightHTML}
        </span>
      )}
      {props.children}
    </button>
  );
}
